import React from "react";
import Link from "next/link";
import {useTranslations} from 'next-intl';
import { Translations } from "@/translations";
import styles from "./Breadcrumb.module.css";

interface UrlData {
  url: string | undefined;
  title?: string | undefined;
}


interface PropsData {
  title: string | undefined;
  backButtonUrl?: string;
  urls?: UrlData[];
}

const breadcrumbFontSize = "clamp(1.5rem, 2.4vw, 2.25rem)";
const backButtonFontSize = "clamp(0.95rem, 1.5vw, 1.1rem)";

const Breadcrumb = ({ title, backButtonUrl, urls }: PropsData) => {
    const t = useTranslations("General");
  const backButtonLabel = t(Translations.General.Back);
    const breadcrumbItems: UrlData[] = [...(urls ?? []), ...(title ? [{ title, url: undefined }] : [])];

  return (
    <>
      <section
        className="blue-bg-3 d-flex align-items-center"
        style={{ padding: "32px 0" }}>
        <div className="container">
          <div className="row">
            <div className="col-xxl-12">
              <div className="page__title-wrapper">
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 16,
                    marginTop: 12,
                  }}
                >
                  {backButtonUrl && (
                    <Link
                      href={backButtonUrl || "#"}
                      aria-label={backButtonLabel}
                      title={backButtonLabel}
                      className={styles.backButton}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        minWidth: 40,
                        minHeight: 40,
                        padding: "8px 12px",
                        borderRadius: 999,
                        border: "1px solid rgba(31, 122, 61, 0.22)",
                        background: "rgba(31, 122, 61, 0.06)",
                        color: "#1f7a3d",
                        fontSize: backButtonFontSize,
                        fontWeight: 700,
                        lineHeight: 1,
                        textDecoration: "none",
                      }}
                    >
                      <span aria-hidden="true" style={{ fontSize: "1.05em" }}>&lt;</span>
                      <span className="d-none d-sm-inline">{backButtonLabel}</span>
                    </Link>
                  )}

                  {breadcrumbItems.length > 0 && (
                    <nav aria-label="breadcrumb">
                      <ol
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 8,
                          margin: 0,
                          padding: 0,
                          listStyle: "none",
                        }}
                      >
                        {breadcrumbItems.map((item, index) => {
                          const isCurrent = index === breadcrumbItems.length - 1;

                          return (
                            <React.Fragment key={`${item.title}-${index}`}>
                              {index > 0 && (
                                <li
                                  aria-hidden="true"
                                  style={{
                                    color: "#7b88a1",
                                    display: "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M4.5 2.25L8.25 6L4.5 9.75"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </li>
                              )}
                              <li aria-current={isCurrent ? "page" : undefined}>
                                {item.url && !isCurrent ? (
                                  <Link
                                    href={item.url}
                                    style={{
                                      color: "#2454c5",
                                      fontSize: breadcrumbFontSize,
                                      fontWeight: 700,
                                      textDecoration: "none",
                                    }}
                                  >
                                    {item.title}
                                  </Link>
                                ) : (
                                  <span
                                    style={{
                                      color: isCurrent ? "#0f172a" : "#55637a",
                                      fontSize: breadcrumbFontSize,
                                      fontWeight: isCurrent ? 800 : 700,
                                    }}
                                  >
                                    {item.title}
                                  </span>
                                )}
                              </li>
                            </React.Fragment>
                          );
                        })}
                      </ol>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Breadcrumb;
